import { keyframes } from '@emotion/react';
import { ThemeConfig } from '../../theme';

/**
 * Design tokens for the public landing page — Stratify ERP.
 *
 * Deliberately self-contained rather than an extension of the app's
 * `ThemeColors`. The five app presets (blue/dark/green/purple/orange) are a
 * *tenant white-label preference* — a property of a logged-in workspace. A
 * logged-out prospect must always see the Stratify brand, not whatever preset
 * is sitting in their localStorage from a previous session. In particular the
 * `dark` preset would invert the light sections the whole page is built on.
 *
 * So: the landing reads from here, never from `useAppTheme()`.
 */
export const landing = {
  // ---- Brand --------------------------------------------------------------
  /** The button/primary blue. Deep enough to carry white text at AA. */
  brand: '#3355e8',
  brandDeep: '#2540c4',
  brandSoft: '#eef2ff',
  violet: '#7b52f5',
  violetSoft: '#f3eeff',

  /**
   * The headline gradient: violet into blue, left to right. Used as a text
   * fill on the hero H1 and nowhere else at body size.
   */
  brandGradient: 'linear-gradient(95deg, #7b52f5 0%, #3b7bf5 55%, #2aa6f0 100%)',
  buttonGradient: 'linear-gradient(135deg, #3f5ff0 0%, #2a40c8 100%)',

  // ---- Light surfaces -----------------------------------------------------
  paper: '#ffffff',
  /** Page background behind white cards — a cool near-white, never pure grey. */
  canvas: '#f6f8fd',
  canvasAlt: '#eef2fa',
  /** The lavender-to-white wash behind the hero. */
  heroWash:
    'radial-gradient(1200px 620px at 18% -8%, #e9e4ff 0%, rgba(233,228,255,0) 62%),' +
    'radial-gradient(1000px 560px at 88% 4%, #dcecff 0%, rgba(220,236,255,0) 58%),' +
    'linear-gradient(180deg, #fbfcff 0%, #ffffff 68%)',

  // ---- Ink ----------------------------------------------------------------
  /** The deep navy band behind the closing CTA and the product mock sidebar. */
  ink: '#0d1a3f',
  inkDeep: '#081231',
  inkGradient: 'linear-gradient(160deg, #142a5e 0%, #0d1a3f 45%, #070f29 100%)',

  // ---- Type ---------------------------------------------------------------
  heading: '#0f172a',
  body: '#4a5568',
  muted: '#7a8699',
  textOnInk: '#f2f6ff',
  textOnInkMuted: 'rgba(214,226,255,0.74)',

  // ---- Lines & elevation --------------------------------------------------
  border: '#e4e9f2',
  borderStrong: '#d3dbe9',
  /**
   * Shadows are blue-tinted, not black. On a cool near-white canvas a neutral
   * black shadow reads as dirt; tinting it to the background's hue is most of
   * the difference between a card that floats and one that looks smudged.
   */
  shadowSm: '0 1px 2px rgba(16,32,74,0.05), 0 2px 8px rgba(16,32,74,0.04)',
  shadowMd: '0 4px 14px rgba(16,32,74,0.07), 0 12px 32px rgba(16,32,74,0.06)',
  shadowLg: '0 18px 48px rgba(16,32,74,0.13), 0 4px 14px rgba(16,32,74,0.06)',
  shadowBrand: '0 10px 28px rgba(51,85,232,0.32)',

  // ---- Rhythm -------------------------------------------------------------
  sectionPy: { xs: 7, md: 11 },
  maxWidth: 1180,
  cardRadius: 16,
  tileRadius: 14,

  // ---- Legacy aliases -----------------------------------------------------
  /**
   * The auth screens (`pages/auth/*`, `components/auth/*`) were built against
   * the previous dark landing and import these names directly. They are kept
   * as aliases — remapped onto the current brand, not frozen at the old
   * values — so those screens keep compiling AND pick up the rebrand, rather
   * than drifting into a second palette nobody maintains.
   *
   * Prefer the names above in new code. Do not delete these without first
   * grepping `src/pages/auth` and `src/components/auth`.
   */
  accent: '#7f9bf5',
  accentMid: '#5b7cf0',
  accentAlt: '#3f5ff0',
  accentGradient: 'linear-gradient(90deg, #8f7bf8 0%, #4f7ff5 100%)',
  heroGradient: 'linear-gradient(160deg, #142a5e 0%, #0d1a3f 45%, #070f29 100%)',
  inkRaised: '#111c42',

  textOnDark: '#f2f6ff',
  textOnDarkMuted: 'rgba(214,226,255,0.74)',
  textOnLight: '#0f172a',
  textOnLightMuted: '#4a5568',
  paperAlt: '#f6f8fd',
  borderLight: '#e4e9f2',
  gridLine: 'rgba(255,255,255,0.05)',

  /**
   * `accent` above is a light blue for use ON INK. On a white surface it is
   * far under 3:1 and fails WCAG AA outright — accent-coloured *text* on light
   * must use this instead (#3355e8, ~5.6:1).
   */
  accentOnLight: '#3355e8',

  glassBg: 'rgba(255,255,255,0.05)',
  glassBorder: '1px solid rgba(255,255,255,0.12)',
  glassBlur: 'blur(12px)',
  glassShadow: '0 2px 12px rgba(0,0,0,0.16)',
} as const;

/**
 * The twelve module tiles and the industry/flow accents are all drawn from
 * this one pastel set, so a tile added later can never invent a new hue.
 * Each entry is [icon foreground, tile background].
 */
export const tints = {
  blue: ['#2f6fed', '#e8f0fe'],
  orange: ['#f08a24', '#fdf0e3'],
  violet: ['#7b52f5', '#f0eafe'],
  teal: ['#0ea5a5', '#e2f6f5'],
  amber: ['#e9a13b', '#fdf3e0'],
  sky: ['#2b93d8', '#e6f3fb'],
  rose: ['#e2556b', '#fdeaed'],
  green: ['#22a06b', '#e3f6ed'],
  indigo: ['#5566e8', '#ebeefe'],
  slate: ['#5b6b86', '#eef1f6'],
  pink: ['#d9539c', '#fceaf4'],
  lime: ['#6aa829', '#eff7e2'],
} as const;

export type TintName = keyof typeof tints;

/** Fluid type scale. `clamp()` avoids per-breakpoint overrides entirely. */
export const landingType = {
  hero: { fontSize: 'clamp(2.35rem, 5.2vw, 3.9rem)', lineHeight: 1.06, letterSpacing: '-0.033em', fontWeight: 800 },
  h2: { fontSize: 'clamp(1.7rem, 3.1vw, 2.45rem)', lineHeight: 1.16, letterSpacing: '-0.026em', fontWeight: 800 },
  h3: { fontSize: 'clamp(1.05rem, 1.5vw, 1.2rem)', lineHeight: 1.3, letterSpacing: '-0.012em', fontWeight: 700 },
  lead: { fontSize: 'clamp(1rem, 1.35vw, 1.14rem)', lineHeight: 1.62, fontWeight: 400 },
  body: { fontSize: '0.95rem', lineHeight: 1.62 },
  small: { fontSize: '0.82rem', lineHeight: 1.5 },
  micro: { fontSize: '0.72rem', lineHeight: 1.45, letterSpacing: '0.07em', fontWeight: 700 },
  /** Legacy alias for `micro`, still imported by the auth screens. */
  eyebrow: { fontSize: '0.72rem', lineHeight: 1.45, letterSpacing: '0.18em', fontWeight: 700 },
} as const;

// ---- Keyframes ------------------------------------------------------------

export const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

export const floatSlow = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50%      { transform: translateY(-16px) rotate(-1.2deg); }
`;

export const drift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
`;

export const shimmer = keyframes`
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

export const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(51,85,232,0.34); }
  70%  { box-shadow: 0 0 0 14px rgba(51,85,232,0); }
  100% { box-shadow: 0 0 0 0 rgba(51,85,232,0); }
`;

/** Draws an SVG path on: pair with pathLength={1} so the dash math is unitless. */
export const drawLine = keyframes`
  from { stroke-dashoffset: 1; }
  to   { stroke-dashoffset: 0; }
`;

/**
 * The pulse that travels a process-flow connector.
 *
 * The 16px throw must equal `ProcessFlowSection`'s `GAP`, since the connector
 * spans exactly one grid gutter — if that gutter changes, change this too or
 * the dot will stop short of (or overshoot) the next step.
 */
export const flowPulse = keyframes`
  0%   { transform: translateX(-2px); opacity: 0; }
  35%  { opacity: 1; }
  100% { transform: translateX(16px); opacity: 0; }
`;

export const caretBlink = keyframes`
  0%, 45%   { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

export const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

export const popIn = keyframes`
  0%   { opacity: 0; transform: scale(.86); }
  60%  { opacity: 1; transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1); }
`;

// ---- Mixins ---------------------------------------------------------------

export const reducedMotion = {
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
};

/** Visible focus ring for controls on light surfaces. */
export const focusRing = {
  '&:focus-visible': { outline: `2px solid ${landing.brand}`, outlineOffset: '2px' },
};

/** Visible focus ring for controls sitting on the ink band. */
export const focusRingOnDark = {
  '&:focus-visible': { outline: '2px solid #9dbcff', outlineOffset: '2px' },
};

/** Text filled with the brand gradient. Headline-size only — never body copy. */
export const gradientText = {
  background: landing.brandGradient,
  backgroundSize: '220% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  color: 'transparent',
  animation: `${drift} 11s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

/**
 * Translucent surface for cards sitting on the ink band.
 *
 * The fill is a vertical white-alpha *gradient*, not a flat tint — it reads as
 * a lit surface rather than a translucent rectangle, which is most of the
 * difference between a glass card that looks designed and one that looks
 * cheap. Kept here because the auth screens import it; the light-surface
 * equivalent is `card` below.
 */
export const glassSurface = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))',
  border: landing.glassBorder,
  backdropFilter: landing.glassBlur,
  WebkitBackdropFilter: landing.glassBlur,
  boxShadow: landing.glassShadow,
  borderRadius: `${landing.cardRadius}px`,
};

/** The standard white card: hairline border, tinted shadow, lift on hover. */
export const card = {
  backgroundColor: landing.paper,
  border: `1px solid ${landing.border}`,
  borderRadius: `${landing.cardRadius}px`,
  boxShadow: landing.shadowSm,
};

export const cardHover = {
  transition: 'transform .34s cubic-bezier(.2,.7,.2,1), box-shadow .34s ease, border-color .34s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: landing.shadowMd,
    borderColor: landing.borderStrong,
  },
  ...reducedMotion,
};

/**
 * Fixed, blue-derived MUI theme so components render on-brand regardless of
 * which app preset the visitor has stored.
 */
export const stratifyLandingConfig: ThemeConfig = {
  id: 'stratify-landing',
  name: 'Stratify Landing',
  brand: {
    name: 'Stratify ERP',
    shortName: 'Stratify',
    tagline: 'One Business. One Platform.',
    logo: '/logo192.png',
    version: '1.0.0',
  },
  colors: {
    primary: landing.brand,
    primaryDark: landing.brandDeep,
    primaryLight: '#7f9bf5',

    secondary: landing.violet,
    secondaryDark: '#5b34c9',
    secondaryLight: '#a78bfa',

    sidebarBg: landing.ink,
    sidebarBgGradient: landing.inkGradient,
    sidebarText: landing.textOnInk,
    sidebarTextMuted: landing.textOnInkMuted,
    sidebarActiveItem: 'rgba(255,255,255,0.15)',
    sidebarHoverItem: 'rgba(255,255,255,0.08)',
    sidebarAccent: '#7f9bf5',
    sidebarAccentGradient: `linear-gradient(180deg, #7f9bf5 0%, ${landing.brandDeep} 100%)`,

    headerBg: landing.paper,
    headerBgGradient: landing.paper,
    headerText: landing.heading,

    background: landing.canvas,
    backgroundPaper: landing.paper,
    backgroundAlt: landing.canvasAlt,

    textPrimary: landing.heading,
    textSecondary: landing.body,

    success: '#15803d',
    warning: '#b45309',
    error: '#b91c1c',
    info: landing.brand,

    divider: landing.border,
    border: landing.border,
  },
};
